import React, { useState, useEffect } from 'react';
import {
  ShoppingBag, Upload, FileText, Loader2, CheckCircle, AlertCircle, ExternalLink, Headphones, BookOpen
} from 'lucide-react';
import { api } from '../../lib/api';

const TYPE_ICONS = {
  ebook: BookOpen,
  consultoria: Headphones,
};

const AdminAddonsPage = () => {
  const [addons, setAddons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const [feedback, setFeedback] = useState({ id: null, kind: '', message: '' });

  const loadAddons = async () => {
    try {
      setIsLoading(true);
      const res = await api.get('/api/admin/addons/config');
      setAddons(res.data?.data?.addons || []);
    } catch (err) {
      console.error('Erro ao carregar addons:', err);
      setFeedback({ id: null, kind: 'error', message: 'Falha ao carregar a configuração.' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAddons();
  }, []);

  const handlePdfUpload = async (addonId, file) => {
    if (!file) return;
    setUploadingId(addonId);
    setFeedback({ id: addonId, kind: '', message: '' });
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/api/admin/addons/${addonId}/pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data?.data;
      setAddons((prev) => prev.map((a) => a.id === addonId
        ? { ...a, pdf_url: data?.pdf_url || a.pdf_url, pdf_file_name: data?.pdf_file_name || a.pdf_file_name, updated_at: new Date().toISOString() }
        : a
      ));
      setFeedback({ id: addonId, kind: 'success', message: 'PDF atualizado com sucesso.' });
    } catch (err) {
      console.error('Erro no upload:', err);
      const detail = err?.response?.data?.detail || err?.message || 'Falha ao enviar arquivo.';
      setFeedback({ id: addonId, kind: 'error', message: typeof detail === 'string' ? detail : 'Falha ao enviar arquivo.' });
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-20 pt-4 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white/60 backdrop-blur-md rounded-3xl border border-gray-200/50 px-6 shadow-sm">
            <div className="flex items-center h-16 sm:h-20 gap-3">
              <div className="w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-500">Administração</p>
                <h1 className="text-base sm:text-lg font-bold text-gray-900">Add-ons</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        <p className="text-sm text-gray-500 mb-6">
          Configure os arquivos disponibilizados após a compra de cada add-on. Para e-books, suba o PDF aqui; quando vazio, o usuário cai em um chat com a equipe.
        </p>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {addons.map((addon) => {
              const Icon = TYPE_ICONS[addon.type] || BookOpen;
              const isEbook = addon.type === 'ebook';
              const isThisFeedback = feedback.id === addon.id && feedback.message;
              return (
                <div key={addon.id} className="bg-white rounded-2xl border border-gray-200 p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-gray-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-bold text-gray-900">{addon.name}</h3>
                          <p className="text-xs text-gray-400 capitalize">{addon.type}</p>
                        </div>
                        <span className="text-xs font-semibold text-gray-500">
                          {(addon.amount_cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </span>
                      </div>

                      {isEbook ? (
                        <div className="mt-4 space-y-2">
                          {addon.pdf_url ? (
                            <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                              <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <span className="text-xs text-gray-700 truncate flex-1">
                                {addon.pdf_file_name || addon.pdf_url.split('/').pop()}
                              </span>
                              <a
                                href={addon.pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700"
                              >
                                Ver <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400">Nenhum PDF cadastrado (fallback: chat com equipe).</p>
                          )}

                          <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 hover:border-amber-300 rounded-xl py-3 px-4 cursor-pointer transition-colors bg-gray-50 hover:bg-amber-50/30">
                            {uploadingId === addon.id ? (
                              <>
                                <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
                                <span className="text-sm text-gray-500">Enviando...</span>
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-500">
                                  {addon.pdf_url ? 'Substituir PDF' : 'Subir PDF do e-book'}
                                </span>
                              </>
                            )}
                            <input
                              type="file"
                              accept="application/pdf"
                              className="hidden"
                              disabled={uploadingId === addon.id}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                e.target.value = '';
                                if (f) handlePdfUpload(addon.id, f);
                              }}
                            />
                          </label>
                        </div>
                      ) : (
                        <p className="mt-3 text-xs text-gray-500">
                          Este add-on é atendido via chat. Nenhuma configuração adicional necessária.
                        </p>
                      )}

                      {isThisFeedback && (
                        <div className={`mt-3 flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${
                          feedback.kind === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}>
                          {feedback.kind === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                          {feedback.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAddonsPage;
